import { useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import {
  Check,
  ChevronRight,
  ChevronsUp,
  ChevronUp,
  Pause,
  Play,
  Flame,
  Plus,
} from 'lucide-react';

import { cn } from '@/lib/cn';
import { motionSafe, springDefault } from '@/lib/motion';
import { DUE_PILL, completedInfo, dueInfo } from '@/features/calendars/backlogSort';
import { useFocus, useFocusControls } from '@/features/time/useFocus';
import { formatClock } from '@/features/time/useTimer';
import { SubtaskComposer } from './SubtaskComposer';
import { formatDuration } from './tree';
import type { Energy, TaskNode } from './types';

interface Props {
  node: TaskNode;
  clientColor: string;
  timezone: string;
  onToggleDone: (task: TaskNode) => void;
  onOpen: (task: TaskNode) => void;
  /** When set, show when the task was finished instead of the due date. */
  showCompletedAt?: boolean;
  /** Review-only rows: done on the plan but reopened (e.g. recurring). */
  completedHistory?: boolean;
}

const ENERGY_LABEL: Record<Energy, string> = {
  high: 'High focus',
  medium: 'Medium focus',
  low: 'Low focus',
};

export function TaskRow({
  node,
  clientColor,
  timezone,
  onToggleDone,
  onOpen,
  showCompletedAt = false,
  completedHistory = false,
}: Props) {
  // Collapsed by default past depth 1, so a deep tree never dumps sixty rows
  // at someone who opened the page to find one thing.
  const [expanded, setExpanded] = useState(node.depth < 1);
  const [addingSubtask, setAddingSubtask] = useState(false);

  const focus = useFocus();
  const controls = useFocusControls();
  const isTiming = focus.data?.active_task_id === node.id && focus.data.mode === 'working';

  const hasChildren = node.children.length > 0;
  const historyDone = completedHistory && Boolean(node.history_completed_at);
  const isDone = node.status === 'done' || historyDone;
  const own = formatDuration(node.estimate_minutes);
  const subtree = formatDuration(node.rollup_estimate_minutes);
  // Only worth showing separately when the branch holds more than this node.
  const showSubtree = hasChildren && subtree && subtree !== own;
  const due = dueInfo(node.due_at, timezone);
  const finishedAt = node.history_completed_at ?? node.completed_at;
  const finished = showCompletedAt ? completedInfo(finishedAt, timezone) : null;

  // The second line costs a row of vertical space on every task, so it only
  // exists when it has something to say. Most rows are one line.
  const hasDetail =
    showSubtree || hasChildren || node.rollup_logged_seconds > 0 || node.billable;
  // A phone cannot afford a title column and a due pill on the same line, and
  // the title is the part you are scanning for. Below `sm` the pill drops to
  // the detail line; above it, it sits in the right-hand cluster.
  const showDetailRow =
    hasDetail || (showCompletedAt ? finished?.at != null : due.at !== null);

  return (
    <li>
      <div
        className={cn(
          'group flex min-h-touch items-center gap-2 rounded-r py-1.5 pr-1.5',
          'border-l-2 pl-2.5 transition-colors duration-press',
          'hover:bg-surface-2',
        )}
        style={{ borderLeftColor: clientColor }}
      >
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={hasChildren ? expanded : undefined}
          aria-label={hasChildren ? (expanded ? 'Collapse' : 'Expand') : undefined}
          disabled={!hasChildren}
          // Fixed footprint whether or not there is a chevron, so titles stay
          // aligned down the column instead of jittering by depth.
          className={cn(
            'flex size-check shrink-0 items-center justify-center rounded-sm',
            hasChildren ? 'text-text-tertiary hover:text-text' : 'invisible',
          )}
        >
          <motion.span
            animate={{ rotate: expanded ? 90 : 0 }}
            transition={motionSafe(springDefault)}
            className="flex"
          >
            <ChevronRight size={14} strokeWidth={1.5} aria-hidden />
          </motion.span>
        </button>

        <button
          type="button"
          onClick={() => {
            if (historyDone && node.status !== 'done') return;
            onToggleDone(node);
          }}
          aria-pressed={isDone}
          aria-label={
            historyDone && node.status !== 'done'
              ? `Completed on plan: open "${node.title}" for details`
              : isDone
                ? `Mark "${node.title}" not done`
                : `Complete "${node.title}"`
          }
          disabled={historyDone && node.status !== 'done'}
          className={cn(
            'pressable flex size-check shrink-0 items-center justify-center rounded-sm border',
            isDone
              ? 'border-success bg-success text-text-on-accent'
              : 'border-border-strong hover:border-accent',
            historyDone && node.status !== 'done' && 'cursor-default opacity-90',
          )}
        >
          {isDone && <Check size={12} strokeWidth={3} aria-hidden />}
        </button>

        <div className="min-w-0 flex-1">
          {/* The title is the affordance for opening the task. A separate
              edit icon would be one more thing to find. */}
          <button
            type="button"
            onClick={() => onOpen(node)}
            title={node.title}
            className={cn(
              'flex w-full items-center gap-1.5 rounded text-left text-body',
              isDone ? 'text-text-tertiary line-through' : 'text-text',
            )}
          >
            <PriorityMark priority={node.priority} />
            <span className="truncate">{node.title}</span>
          </button>

          {showDetailRow && (
            <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-small text-text-tertiary">
              {showCompletedAt && finished?.at && (
                <span title={finished.full} className="rounded-md text-label font-medium">
                  {finished.label}
                </span>
              )}

              {!showCompletedAt && due.at && (
                <span
                  title={due.full}
                  className={cn(
                    'rounded-md text-label font-medium sm:hidden',
                    due.tone === 'overdue' && 'text-danger',
                    due.tone === 'today' && 'text-warning',
                  )}
                >
                  {due.label}
                </span>
              )}

              {showSubtree && (
                // The branch total, distinct from this task's own work. "2h of
                // my own work" and "2h across eleven subtasks" are different
                // situations and the row should not conflate them.
                <span className="tabular" title="Total including subtasks">
                  {subtree} total
                </span>
              )}

              {hasChildren && (
                <span className="tabular">
                  {node.rollup_done_count}/{node.rollup_total_count} done
                </span>
              )}

              {node.rollup_logged_seconds > 0 && (
                <span className="tabular" title="Time logged, including subtasks">
                  {formatClock(node.rollup_logged_seconds)} logged
                </span>
              )}

              {node.billable && <span className="text-accent">Billable</span>}
            </div>
          )}
        </div>

        {node.energy !== 'medium' && (
          // Icon rather than the word: on a list where most tasks carry an
          // energy, spelling it out turns a signal into wallpaper.
          <span
            className={cn(
              'shrink-0',
              node.energy === 'high' ? 'text-warning' : 'text-text-tertiary opacity-60',
            )}
            title={ENERGY_LABEL[node.energy]}
          >
            <Flame
              size={13}
              strokeWidth={1.5}
              fill={node.energy === 'high' ? 'currentColor' : 'none'}
              aria-hidden
            />
            <span className="sr-only">{ENERGY_LABEL[node.energy]}</span>
          </span>
        )}

        {!showCompletedAt && due.at && (
          <span
            title={due.full}
            className={cn(
              'hidden shrink-0 rounded-md px-1.5 py-0.5 text-label font-medium sm:inline-block',
              DUE_PILL[due.tone],
            )}
          >
            {due.label}
          </span>
        )}

        {showCompletedAt && finished?.at && (
          <span
            title={finished.full}
            className="hidden shrink-0 rounded-md px-1.5 py-0.5 text-label font-medium text-text-secondary sm:inline-block"
          >
            {finished.label}
          </span>
        )}

        {/* Fixed-width, right-aligned: estimates form a column you can read
            down in one pass instead of hunting for them mid-sentence. */}
        <span className="w-[3.25rem] shrink-0 text-right text-small tabular text-text-tertiary">
          {own ?? ''}
        </span>

        <button
          type="button"
          onClick={() => {
            // Expand first: a new child dropped into a collapsed row would
            // look like nothing happened.
            setExpanded(true);
            setAddingSubtask(true);
          }}
          aria-label={`Add subtask to "${node.title}"`}
          title="Add subtask"
          className={cn(
            'pressable flex size-control shrink-0 items-center justify-center rounded-full',
            'text-text-tertiary opacity-0 transition-colors duration-press',
            'hover:bg-surface-3 hover:text-text',
            'group-hover:opacity-100 focus-visible:opacity-100',
            '[@media(hover:none)]:opacity-100',
          )}
        >
          <Plus size={13} strokeWidth={1.5} aria-hidden />
        </button>

        <button
          type="button"
          onClick={() =>
            isTiming
              ? controls.pause.mutate()
              : controls.start.mutate({ task_id: node.id })
          }
          disabled={isDone}
          aria-label={isTiming ? `Pause "${node.title}"` : `Start focus on "${node.title}"`}
          className={cn(
            'pressable flex size-control shrink-0 items-center justify-center rounded-full',
            // Hidden until hover on a pointer device, always present on touch
            // where there is no hover to reveal it. Done rows keep the space so
            // the estimate column does not shift when a task is completed.
            isDone && 'invisible',
            isTiming
              ? 'bg-accent text-text-on-accent'
              : 'text-text-tertiary opacity-0 hover:bg-surface-3 hover:text-text group-hover:opacity-100 focus-visible:opacity-100 [@media(hover:none)]:opacity-100',
          )}
        >
          {isTiming ? (
            <Pause size={13} strokeWidth={1.5} aria-hidden />
          ) : (
            <Play size={13} strokeWidth={1.5} aria-hidden />
          )}
        </button>
      </div>

      {addingSubtask && (
        <div className="ml-5 border-l border-border py-1 pl-3 pr-1.5">
          <SubtaskComposer
            parentId={node.id}
            autoFocus
            onCancel={() => setAddingSubtask(false)}
            placeholder={`Subtask of "${node.title}"`}
          />
        </div>
      )}

      <AnimatePresence initial={false}>
        {hasChildren && expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={motionSafe(springDefault)}
            className="overflow-hidden"
          >
            <ul className="ml-5 border-l border-border pl-1">
              {node.children.map((child) => (
                <TaskRow
                  key={child.id}
                  node={child}
                  clientColor={clientColor}
                  timezone={timezone}
                  onToggleDone={onToggleDone}
                  onOpen={onOpen}
                />
              ))}
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </li>
  );
}

/**
 * Priority is marked only when it is above the baseline.
 *
 * A glyph on every row -- including the three quarters of a backlog that sit
 * at "normal" -- is a glyph that stops meaning anything.
 */
function PriorityMark({ priority }: { priority: TaskNode['priority'] }) {
  if (priority === 'urgent') {
    return (
      <span className="shrink-0 text-danger" title="Urgent">
        <ChevronsUp size={14} strokeWidth={2} aria-hidden />
        <span className="sr-only">Urgent</span>
      </span>
    );
  }
  if (priority === 'high') {
    return (
      <span className="shrink-0 text-warning" title="High priority">
        <ChevronUp size={14} strokeWidth={2} aria-hidden />
        <span className="sr-only">High priority</span>
      </span>
    );
  }
  return null;
}
